import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Organization, OrganizationSettings, Role, User, UserContext, UserDetail } from './user.model';
import { catchError, map, of } from 'rxjs';
import { Account } from '../api/account.model';

@Injectable({
    providedIn: 'root',
})
export class UsersApiService {
    constructor(private http: HttpClient) {}

    getCurrentUser$() {
        return this.http.get<User>('v1/users/current').pipe(
            map((user) => ({ ...user, roleLabel: this.roleToLabel(user.role) })),
            catchError(() => of(null))
        );
    }

    getUserContext$() {
        return this.http.get<UserContext>('v1/users/current/context');
    }

    getUsers$(id: string, isGroup: boolean = false) {
        return this.http
            .get<(Omit<User, 'roleLabel'> & { user_id?: string })[]>(`v1/${isGroup ? 'accounts' : 'organizations'}/${id}/members`)
            .pipe(map((users) => users.map((user): User => ({
                ...user,
                id: user.id ?? user.user_id ?? '',
                roleLabel: this.roleToLabel(user.role),
            }))));
    }

    createUser$(email: string, type: 'account' | 'org', id: string, role?: string) {
        if (type === 'org') {
            return this.http.post(`v1/organizations/${id}/members`, { email, ...(role && { role }) });
        }
        return this.http.post('v1/invitations', { email, type, account_id: id, ...(role && { role }) });
    }

    getOrganizations$() {
        return this.http.get<Organization[]>('v1/organizations');
    }

    getOrganizationById$(id: string) {
        return this.http.get<Organization>(`v1/organizations/${id}`);
    }

    getAccountOrganizations$(accountId: string) {
        return this.http.get<Organization[]>(`v1/accounts/${accountId}/organizations`);
    }

    getAccounts$() {
        return this.http.get<Account[]>('v1/accounts');
    }

    createAccount$(name: string) {
        return this.http.post<Account>('v1/accounts', { name });
    }

    getUserById$(userId: string, orgId?: string) {
        if (orgId) {
            return this.http.get<UserDetail>(`v1/organizations/${orgId}/members/${userId}`);
        }
        return this.http.get<UserDetail>(`v1/users/${userId}`);
    }

    removeAdminFromOrganization$(orgId: string, userId: string) {
        return this.http.delete(`v1/organizations/${orgId}/members/${userId}`);
    }

    removeMemberFromAccount$(accountId: string, userId: string) {
        return this.http.delete(`v1/accounts/${accountId}/members/${userId}`);
    }

    roleToLabel(role: Role) {
        return (
            {
                ROOT_ADMIN: 'Root Admin',
                SYSTEM_ADMIN: 'System Admin',
                ACCOUNT_ADMIN: 'Account Admin',
                ORG_ADMIN: 'Organization Admin',
                ORG_MEMBER: 'Org Member',
                USER: 'User',
                '': '',
            }[role] ?? role
        );
    }

    createOrganization$(name: string, account_id: string, id?: string) {
        return this.http.post<Organization>('v1/organizations', { name, account_id, ...(id ? { id } : {}) });
    }

    deleteOrganization$(orgId: string) {
        return this.http.delete(`v1/organizations/${orgId}`);
    }

    // Partial update — only the keys sent are written. ACCOUNT_ADMIN only.
    updateOrganizationSettings$(orgId: string, settings: Partial<OrganizationSettings>) {
        return this.http.patch<OrganizationSettings>(`v1/organizations/${orgId}/settings`, settings);
    }
}
